import { Close as CloseIcon } from '@mui/icons-material';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Alert
} from '@mui/material';
import React from 'react';

import { ServiceDetailModalProps } from './types';

const ServiceDetailModal: React.FC<ServiceDetailModalProps> = ({
  open,
  service,
  loading,
  cartItem,
  onClose
}) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          height: '80vh',
          maxHeight: '800px'
        }
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">
          {loading ? 'Loading...' : service?.name || 'Service Details'}
        </Typography>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <Typography>Loading service details...</Typography>
          </Box>
        ) : service ? (
          <Box>
            {/* Company Header with Logo and Name */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              {service.brand_logo_path && (
                <Box
                  component="img"
                  src={service.brand_logo_path}
                  alt={service.brand_name || service.owner_company || 'Provider'}
                  sx={{ height: 48, width: 'auto', maxWidth: 120, borderRadius: 1 }}
                />
              )}
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  {service.brand_name || service.owner_company || 'TechAdds Platform'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {service.brand_description || 'Professional IT Services Provider'}
                </Typography>
              </Box>
            </Box>

            {/* Service Details */}
            <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
              {service.name}
            </Typography>

            <Typography variant="body1" sx={{ mb: 3 }}>
              {service.description}
            </Typography>

            {service.category && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Category: {service.category}
                </Typography>
              </Box>
            )}

            {service.one_time_charge && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  One-Time Charge (NRC): ${(service.one_time_charge / 100).toFixed(2)}
                </Typography>
              </Box>
            )}

            {/* Selected Perk Information */}
            {cartItem?.selected_perk_name && (
              <Box sx={{ mb: 3, p: 2, bgcolor: 'success.50', borderRadius: 1, border: 1, borderColor: 'success.200' }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold', color: 'success.main' }}>
                  Selected Perk:
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {cartItem.selected_perk_name}
                </Typography>
              </Box>
            )}

            {/* Cart Item Specific Information */}
            {cartItem && (
              <Box sx={{ mb: 3, p: 2, bgcolor: 'info.50', borderRadius: 1 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                  Cart Item Details:
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Typography variant="body2">
                    Quantity: {cartItem.quantity}
                  </Typography>
                  {cartItem.selected_address_label && (
                    <Typography variant="body2">
                      Address: {cartItem.selected_address_label}
                    </Typography>
                  )}
                  <Typography variant="body2">
                    Status: {cartItem.status}
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
        ) : (
          <Alert severity="error">
            Failed to load service details. Please try again.
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ServiceDetailModal;